import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import ReplayIcon from '@mui/icons-material/Replay';
import Popover from '@mui/material/Popover';
import { enqueueSnackbar } from 'notistack';
import {
  useDeclinePreviousStatus,
  useRestorePreviousStatus,
} from 'api/bitedb/entities/entities.hooks';
import { GlobalContext } from 'providers/GlobalProvider';

const STATUS_LABELS: Record<string, string> = {
  cleared: 'Cleared',
  quarantined: 'Quarantined',
  barred: 'Barred',
  not_reviewed: 'Not Reviewed',
  new: 'New',
  removed: 'Removed',
};

export function formatStatusFlagLabel(
  statusFlag: string | null | undefined,
): string {
  if (!statusFlag) return '';
  return STATUS_LABELS[statusFlag] || statusFlag;
}

interface PreviousStatusChipProps {
  previousStatusFlag: string | null | undefined;
  associationId?: string | null;
  databaseId?: string;
  disabled?: boolean;
}

const PreviousStatusChip: React.FC<PreviousStatusChipProps> = ({
  previousStatusFlag,
  associationId,
  databaseId,
  disabled = false,
}) => {
  const { cur_user } = useContext(GlobalContext);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const restoreMutation = useRestorePreviousStatus(
    databaseId,
    cur_user?.selected_team?.id,
  );
  const declineMutation = useDeclinePreviousStatus(
    databaseId,
    cur_user?.selected_team?.id,
  );
  const isPending = restoreMutation.isPending || declineMutation.isPending;

  const clearCloseTimer = useCallback(() => {
    if (closeTimerRef.current) {
      clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      clearCloseTimer();
    };
  }, [clearCloseTimer]);

  const openPopover = useCallback(
    (event: React.MouseEvent<HTMLElement> | React.FocusEvent<HTMLElement>) => {
      if (disabled || !associationId) return;
      clearCloseTimer();
      setAnchorEl(event.currentTarget);
    },
    [associationId, clearCloseTimer, disabled],
  );

  const scheduleClose = useCallback(() => {
    clearCloseTimer();
    closeTimerRef.current = setTimeout(() => {
      setAnchorEl(null);
    }, 150);
  }, [clearCloseTimer]);

  const handleRestore = useCallback(() => {
    if (!associationId || isPending) return;
    restoreMutation.mutate([String(associationId)], {
      onSuccess: (result) => {
        enqueueSnackbar(
          result?.message || 'Restored previous status for this entity',
          { variant: 'success' },
        );
        setAnchorEl(null);
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error?.message || 'Failed to restore previous status',
          { variant: 'error' },
        );
      },
    });
  }, [associationId, isPending, restoreMutation]);

  const handleDecline = useCallback(() => {
    if (!associationId || isPending) return;
    declineMutation.mutate([String(associationId)], {
      onSuccess: (result) => {
        enqueueSnackbar(
          result?.message || 'Declined previous status for this entity',
          { variant: 'success' },
        );
        setAnchorEl(null);
      },
      onError: (error: any) => {
        enqueueSnackbar(
          error?.message || 'Failed to decline previous status',
          { variant: 'error' },
        );
      },
    });
  }, [associationId, declineMutation, isPending]);

  if (!previousStatusFlag) return null;

  const label = formatStatusFlagLabel(previousStatusFlag);
  if (!label) return null;

  const open = Boolean(anchorEl) && Boolean(associationId) && !disabled;
  const interactive = Boolean(associationId) && !disabled;

  return (
    <>
      <span
        className={`bdb-previous-status-chip${
          interactive ? ' bdb-previous-status-chip--interactive' : ''
        }`}
        onMouseEnter={openPopover}
        onMouseLeave={scheduleClose}
        onFocus={openPopover}
        onBlur={scheduleClose}
        tabIndex={interactive ? 0 : undefined}
        aria-haspopup={interactive ? 'dialog' : undefined}
        aria-expanded={open}
      >
        <ReplayIcon className='bdb-previous-status-chip-icon' />
        was {label}
      </span>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        disableRestoreFocus
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          className: 'bdb-previous-status-popover',
          onMouseEnter: clearCloseTimer,
          onMouseLeave: scheduleClose,
        }}
        sx={{
          pointerEvents: 'none',
          '& .MuiPaper-root': { pointerEvents: 'auto' },
        }}
      >
        <div className='bdb-previous-status-popover-body'>
          <div className='bdb-previous-status-popover-title'>
            Previous status: {label}
          </div>
          <div className='bdb-previous-status-popover-hint'>
            Applies to this entity only
          </div>
          <div className='bdb-previous-status-popover-actions'>
            <button
              type='button'
              className='btn-primary'
              style={{ height: '32px', padding: '0 12px', fontSize: '12px' }}
              disabled={isPending}
              onClick={handleRestore}
            >
              Restore
            </button>
            <button
              type='button'
              className='btn-outline'
              style={{ height: '32px', padding: '0 12px', fontSize: '12px' }}
              disabled={isPending}
              onClick={handleDecline}
            >
              Decline
            </button>
          </div>
        </div>
      </Popover>
    </>
  );
};

export default PreviousStatusChip;
